const SCROLL_ANIMATION_TRIGGER_CLASSNAME = "scroll-trigger";
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = "scroll-trigger--offscreen";
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = "animate--zoom-in";
const SCROLL_ANIMATION_CANCEL_CLASSNAME = "scroll-trigger--cancel";

function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    if (element.isIntersecting) {
      const elementTarget = element.target;
      if (
        elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)
      ) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (elementTarget.hasAttribute("data-cascade"))
          elementTarget.setAttribute("style", `--animation-order: ${index};`);
      }
      observer.unobserve(elementTarget);
    } else {
      element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

function initializeScrollAnimationTrigger(
  rootEl = document,
  isDesignModeEvent = false
) {
  const animationTriggerElements = Array.from(
    rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME)
  );
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add("scroll-trigger--design-mode");
    });
    return;
  }

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: "0px 0px -50px 0px",
  });
  animationTriggerElements.forEach((element) => observer.observe(element));
}

// PERF-02: Single shared scroll handler for all zoom-in elements.
// Previously, each element added its own window scroll listener, causing
// handler count to grow linearly with element count (N listeners for N elements).
// Now uses one listener that iterates only visible elements.
// Estimated gain: On a page with N zoom-in images, reduces scroll handler
// invocations from N × (scrolls/100ms) to 1 × (scrolls/100ms).
function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  const animationTriggerElements = Array.from(
    document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME)
  );

  if (animationTriggerElements.length === 0) {
    window.__scrollZoomCleanup?.();
    return;
  }

  window.__scrollZoomCleanup?.();

  const scaleAmount = 0.2 / 100;
  const visibilityStates = new Map();

  // Track visibility of each element via a single IntersectionObserver
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      visibilityStates.set(entry.target, entry.isIntersecting);
    });
  });

  animationTriggerElements.forEach((element) => {
    visibilityStates.set(element, false);
    observer.observe(element);

    // Set initial value
    element.style.setProperty(
      "--zoom-in-ratio",
      1 + scaleAmount * calculateElementVisibilityPercentage(element)
    );
  });

  // Single shared scroll handler for ALL zoom-in elements
  const scrollHandler = throttle(() => {
    animationTriggerElements.forEach((element) => {
      if (!visibilityStates.get(element)) return;
      element.style.setProperty(
        "--zoom-in-ratio",
        1 + scaleAmount * calculateElementVisibilityPercentage(element)
      );
    });
  }, 100);

  window.addEventListener(
    "scroll",
    scrollHandler,
    { passive: true }
  );

  window.__scrollZoomCleanup = () => {
    window.removeEventListener("scroll", scrollHandler, { passive: true });
    observer.disconnect();
    window.__scrollZoomCleanup = null;
  };

  if (Shopify.designMode) {
    document.addEventListener("shopify:section:unload", () => {
      if (document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME).length === 0) {
        window.__scrollZoomCleanup?.();
      }
    });
  }
}

function calculateElementVisibilityPercentage(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    return 100;
  }

  const distance = scrollY + viewportHeight - elementPositionY;
  let percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

window.addEventListener("DOMContentLoaded", () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
});

if (Shopify.designMode) {
  document.addEventListener("shopify:section:load", (event) =>
    initializeScrollAnimationTrigger(event.target, true)
  );
  document.addEventListener("shopify:section:reorder", () =>
    initializeScrollAnimationTrigger(document, true)
  );
}
